import React, {useEffect, useMemo, useRef, useState} from 'react';
import {makeStyles} from '@material-ui/core';
import {Colors} from '../../../styles/Colors';
import classNames from 'classnames';
import AnimatedNumberText from '../../../components/AnimatedNumber/AnimatedNumber';
import {TimeUnitsDisplay} from "./TimeUnitsDisplay";
import CustomizedTooltip from "../../../components/Tooltip/Tooltip";

export type CallStatisticTileProps = {
    value: string,
    textLine: string,
    isLoading: boolean,
    valueClass?: string,
    dataTestId?: string;
    padValue?: boolean;
    preventShowDash?: boolean;
    hideThousands?: boolean;
    inLine?: boolean
};

export function numberWithSpaces(x: number | undefined) {
    const input = (Math.ceil(x ?? 0) + '')
        .replaceAll('.', '')
        .replaceAll(',', '')
        .replaceAll(' ', '')
        .trim();

    const putSpaceAfterChars = 3;
    const stringParts = Array(Math.ceil(input.length / putSpaceAfterChars));
    if (stringParts.length) {
        stringParts[0] = input.slice(0, input.length % putSpaceAfterChars || putSpaceAfterChars);
        for (let i = 1, index = stringParts[0].length; index < input.length; i++) {
            stringParts[i] = input.slice(index, index += putSpaceAfterChars);
        }
    }

    return stringParts.join(' ').trim();
}

export const useStyles = makeStyles(() => ({
    container: {
        width: 212,
        height: 57,
        padding: '24px 0px',
        display: 'flex',
        flexDirection: 'column'
    },
    cropper: {

        textWrap: 'auto',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        flexDirection: 'column'

    },


    dots: {
        position: 'absolute',
        zIndex: 999,
        background: Colors.White,
        color: Colors.Black,
        width: 40
    },

    valueLabelTopDiv: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center'
    },

    croppedValueLabelTopDiv: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-start'
    },
    valueLabel: {
        width: '100%',
        height: 39,
        fontWeight: 300,
        fontSize: 28,
        lineHeight: '38.28px',

        "& hr": {
            marginBlockStart: 'unset',
            marginBlockEnd: 'unset',
            marginInlineStart: 'unset',
            marginInlineEnd: 'unset',
            height: 2,
            marginTop: 5
        },

        '& span': {
            fontWeight: 300,
            fontSize: 28,
            height: 39,
            margin: 0,
            padding: 0
        }
    },
    valueLabelBottomDiv: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        height: 39,

        '& span': {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
        }
    },
    containerLoading: {
        marginTop: -18
    },
    valueLabelPercentage: {
        fontSize: '21px !important',
        marginTop: 3
    },
    textLabel1: {
        width: '100%',
        height: 16,
        verticalAlign: 'middle',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        fontWeight: 700,
        fontSize: 14,
        lineHeight: '16px',
        color: Colors.Black,
        marginTop: 2,
        opacity: 0.6
    },
    textLabel2: {
        width: '100%',
        height: 16,
        verticalAlign: 'middle',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        fontWeight: 400,
        fontSize: 14,
        lineHeight: '16px',
        color: Colors.Black,
        marginTop: 2,
        opacity: 0.5,
        textAlign: 'center'
    },
    timeMarkerLabel: {
        marginRight: 7,
        marginLeft: 2,

        '& span': {
            fontWeight: 300,
            fontSize: 20,
            color: Colors.Gray5,
            marginBottom: -3
        }
    }
}));


function extractTextFromDiv(root: HTMLElement): string[] {
    const textValues: string[] = [];

    function traverse(node: Node): void {
        if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as HTMLElement;
            const tag = element.tagName.toLowerCase();

            const allowedTags = ['div', 'span', 'p', 'b', 'i', 'strong', 'em'];

            if (allowedTags.includes(tag)) {
                if (element.childNodes.length === 0) {
                    const text = element.textContent?.trim();
                    if (text) textValues.push(text);
                } else {
                    element.childNodes.forEach(child => traverse(child));
                }
            }
        } else if (node.nodeType === Node.TEXT_NODE) {
            const trimmedText = node.textContent?.trim();
            if (trimmedText) {
                textValues.push(trimmedText);
            }
        }
    }

    traverse(root);
    return textValues;
}

export const CallStatisticTile: React.VFC<CallStatisticTileProps> = (
    {
        value,
        textLine,
        isLoading,
        valueClass,
        dataTestId,
        padValue = true,
        preventShowDash = false,
        hideThousands = false,
        inLine = false
    }) => {

    const classes = useStyles();
    const ref = useRef<HTMLDivElement | null>(null);

    const [tooltipText, setTooltipText] = useState('')
    const [disableHoverListener, setDisableHoverListener] = useState(true)
    const [isVisible, setIsVisible] = useState(false);


    const isDecimalPercentValue = useMemo(() => {
        return (value || '').split(/[,.:]/).length >= 2 || value.endsWith('%');
    }, [value]);

    const validationRule = (arr: string[], index: number, v: string) => {
        return (arr.length == 3 && index === 2 && +v === 0 && +arr[0] === 0 && +arr[1] === 0)
            || (arr.length == 2 && index === 1 && +v === 0 && +arr[0] === 0)
    }

    useEffect(() => {
        const el = ref.current;
        if (!el) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                }
            },
            {
                threshold: 0.1,
            }
        );

        observer.observe(el);

        return () => {
            if (el) observer.unobserve(el);
        };
    }, []);

    useEffect(() => {
        if (!isVisible) return;

        const el = ref.current;
        if (!el) return;

        const checkEllipsis = () => {
            const hasEllipsis = el.scrollWidth > el.clientWidth;
            setDisableHoverListener(!hasEllipsis);

            if (el) {
                const texts = extractTextFromDiv(el);
                setTooltipText(texts.join(' '));
            }

        };

        const timeout = setTimeout(checkEllipsis, 300);

        return () => clearTimeout(timeout);
    }, [value, isLoading, isVisible]);

    const maxLength = (value || '').split(/[,.:]/).filter(e => e && e.length).length || 0;

    return (<div className={classNames(classes.container)} data-testid={dataTestId}>
        <CustomizedTooltip title={tooltipText} disableHoverListener={disableHoverListener} copy={false}
                           interactive={false}>
            <div ref={ref} className={classNames(classes.valueLabel, valueClass, classes.cropper)}>
                {
                    isDecimalPercentValue
                        ? (<div
                            className={disableHoverListener ? classes.valueLabelTopDiv : classes.croppedValueLabelTopDiv}>
                            {
                                (value || '').split(/[,.:]/).filter(e => e && e.length).map((v, index, arr) => {
                                    if (v.endsWith('%')) {
                                        return (
                                            <div key={'split_index_' + index}
                                                 className={classNames(classes.valueLabelPercentage,
                                                     valueClass,
                                                     isLoading && !preventShowDash && classes.containerLoading,
                                                     classes.valueLabelBottomDiv)}
                                            >
                                                {isLoading && !preventShowDash
                                                    ? ''
                                                    : v.startsWith('0')
                                                        ? v.substring(1)
                                                        : ('.' + v)
                                                }

                                                {index === maxLength - 1 && !disableHoverListener && (
                                                    <div> ... </div>
                                                )}

                                            </div>);
                                    } else {
                                        return (


                                            <div key={'splitted_index_container_' + index}
                                                 className={disableHoverListener ? classes.valueLabelTopDiv : classes.croppedValueLabelTopDiv}>
                                                {
                                                    (+v > 0) || validationRule(arr, index, v) || (value.indexOf('%') !== -1) ? (

                                                        <AnimatedNumberText
                                                            className={classNames(
                                                                valueClass,
                                                                isLoading && !preventShowDash && classes.containerLoading,
                                                                classes.valueLabelBottomDiv
                                                            )}
                                                            value={parseFloat(v.replace(' ', ''))}
                                                            dataTestId="animated-number-value"
                                                            dataQa="animated-number-value"
                                                            showLongDash={isLoading && !preventShowDash}
                                                            formatValue={(v) => padValue ?
                                                                v.toFixed(0).padStart(2, '0') :
                                                                v.toFixed(0)
                                                            }
                                                            key={'split_index_sub3_' + index}
                                                            hideThousands={hideThousands}
                                                        />

                                                    ) : (<></>)
                                                }

                                                {
                                                    (value.indexOf(':') !== -1) &&
                                                    ((+v > 0) || validationRule(arr, index, v)) &&
                                                    (!isLoading || (isLoading && preventShowDash)) && (

                                                        <TimeUnitsDisplay
                                                            key={'split_index_sub2_' + index}
                                                            index={index}
                                                            arrLength={arr.length}
                                                        />

                                                    )
                                                }

                                                {index === maxLength - 1 && !disableHoverListener && (
                                                    <div className={classes.dots}> ...</div>
                                                )}

                                            </div>
                                        );
                                    }


                                })
                            }
                        </div>)
                        : (<div
                            className={classNames(classes.valueLabel, disableHoverListener ? classes.valueLabelTopDiv : classes.croppedValueLabelTopDiv, valueClass)}>
                            <AnimatedNumberText
                                className={classNames(
                                    isLoading && !preventShowDash && classes.containerLoading,
                                    classes.valueLabelBottomDiv,
                                    !isLoading && valueClass
                                )}
                                hideThousands={hideThousands}
                                value={parseFloat(value.replace(' ', ''))}
                                dataTestId="animated-number-value"
                                dataQa="animated-number-value"
                                showLongDash={isLoading && !preventShowDash}
                                formatValue={!hideThousands ? (v) => numberWithSpaces(v) : undefined}
                                inLine={inLine}
                            />
                        </div>)
                }
            </div>
        </CustomizedTooltip>

        <div className={classes.textLabel2}>
            {textLine}
        </div>
    </div>);
};